import { MainApi } from 'api/MainApi'
import { RobotApi, RobotModeMetadata } from 'api/RobotApi'
import clsx from 'clsx'
import { Button } from 'components/Button/Button'
import ConfirmDialog from 'components/ConfirmDialog/ConfirmDialog'
import Dropdown, { IOption } from 'components/Dropdown/Dropdown'
import { useServiceTabActionLoading } from 'hooks/useServiceTabActionLoading'
import { canShowRobotRebootControl, useUser } from 'hooks/user'
import { ReactComponent as ArrowSvg } from 'images/icons/arrow_select.svg'
import { ReactComponent as FacilitySvg } from 'images/icons/facility.svg'
import { ReactComponent as FirmwareUpdateSvg } from 'images/icons/firmware_update.svg'
import { ReactComponent as LocalizationSvg } from 'images/icons/location-bold.svg'
import { ReactComponent as MappingSvg } from 'images/icons/map-bold.svg'
import { ReactComponent as MapUpdateSvg } from 'images/icons/map-update.svg'
import { ReactComponent as RoutingSvg } from 'images/icons/route.svg'
import { ReactComponent as SatelliteSvg } from 'images/icons/satellite.svg'
import { ReactComponent as UpdateSvg } from 'images/icons/update.svg'
import { useEffect, useMemo, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useDispatch, useSelector } from 'react-redux'
import { RobotActions } from 'store/actions/robot'
import {
  selectRobot,
  selectRobotActiveMode,
  selectRobotCatalogOperation,
  selectRobotTelemetry,
} from 'store/selectors/robot'
import store from 'store/store'
import { robotThunks } from 'store/thunks/robot'
import { CATALOG_OPERATION_TIMEOUT_MS } from 'utils/catalogOperationSettlement'
import { ROBOT_COMMANDS, ROBOT_LOCALIZATION_COMMAND, ROBOT_MODES, ROBOT_NETWORK_STATUSES } from 'utils/constants'
import type { RootState } from 'utils/interfaces'

import NtripSettingsDialog from './NtripSettingsDialog'
import ServiceCalibCatalog, { ServiceCalibCatalogProps } from './ServiceCalibCatalog'
import ServiceReportRow from './ServiceReportRow'
import MapRecordingModal from './StartMapRecordingModal/MapRecordingModal'
import { MappingFormData } from './StartMapRecordingModal/MapRecordingView/types'
import { setCatalogOperationTimeoutNotice, useCatalogOperationTimeoutActionKey } from './catalogOperationTimeoutNotice'
import { writeCrossTabCatalogModeBusy } from './catalogServiceCrossTab'
import { deploymentTasksBlockedReason } from './deploymentTasksLock'
import { isNtripConnected } from './ntripApplyConfirmation'
import { resolveConfirmedCatalogServiceKey } from './serviceCalibLotService'
import { serviceModeActionKey } from './serviceModeActionKeys'
import { useModeSwitchConfirmWait } from './useModeSwitchConfirmWait'

import style from './Service.module.css'
import calibStyle from './ServiceCalibCatalog.module.css'

export enum LOCALIZATION_MODES {
  UNDEFINED = 'Undefined',
  LIDAR_LOCALIZATION = 'SLAM based',
  GNSS_BASED_LOCALIZATION = 'GNSS based',
  COMBINED_LOCALIZATION = 'Combined',
}

export const localiazationModeMapping: Record<number, LOCALIZATION_MODES> = {
  0: LOCALIZATION_MODES.UNDEFINED,
  1: LOCALIZATION_MODES.LIDAR_LOCALIZATION,
  2: LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION,
  3: LOCALIZATION_MODES.COMBINED_LOCALIZATION,
}

const localiazationModeToRobotModesMapping: Record<LOCALIZATION_MODES, ROBOT_MODES> = {
  [LOCALIZATION_MODES.UNDEFINED]: ROBOT_MODES.MODE_NOT_SET,
  [LOCALIZATION_MODES.LIDAR_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION,
  [LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION,
  [LOCALIZATION_MODES.COMBINED_LOCALIZATION]: ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION,
}

const ROUTE_NAME_ALLOWED_PATTERN = /^[A-Za-z0-9_/\-() ]+$/

const Service = ({ serviceCalib }: { serviceCalib?: Omit<ServiceCalibCatalogProps, 'isOnline'> }) => {
  const activeMode = useSelector(selectRobotActiveMode)
  const robot = useSelector(selectRobot)
  const catalogOperation = useSelector(selectRobotCatalogOperation)
  const modeSwitchOptimistic = useSelector((state: RootState) => state.robot.serviceModeSwitchOptimistic)
  const dispatch = useDispatch()
  const { t } = useTranslation()
  const telemetry = useSelector(selectRobotTelemetry)
  const { state: userState } = useUser() ?? { state: { audience: null } }
  const canSeeRebootButton = canShowRobotRebootControl(userState)

  const [confirmStartMappingDialog, setConfirmStartMappingDialog] = useState(false)
  const [allowForceBaseRecord, setAllowForceBaseRecord] = useState(false)
  const [stopMappingDialog, setStopMappingDialog] = useState(false)
  const [stopMappingBusy, setStopMappingBusy] = useState(false)
  const mapRecordingWait = useModeSwitchConfirmWait()
  const setMapRecordingError = mapRecordingWait.setError
  const [confirmStartRoutingDialog, setConfirmStartRoutingDialog] = useState(false)
  const [stopRouteRecordingDialog, setStopRouteRecordingDialog] = useState(false)
  const [stopRouteRecordingBusy, setStopRouteRecordingBusy] = useState(false)
  const routeRecordingWait = useModeSwitchConfirmWait()
  const setRouteRecordingError = routeRecordingWait.setError
  const [confirmSwitchLocalizationDialog, setConfirmSwitchLocalizationDialog] = useState(false)
  const [confirmUpdatingFirmwareDialog, setConfirmUpdatingFirmwareDialog] = useState(false)
  const [confirmRebootDialog, setConfirmRebootDialog] = useState(false)
  const [confirmSwitchLidarMapDialog, setConfirmSwitchLidarMapDialog] = useState(false)
  const [confirmSwitchFacilityDialog, setConfirmSwitchFacilityDialog] = useState(false)
  const [ntripDialogOpen, setNtripDialogOpen] = useState(false)
  const [serviceCalibSectionExpanded, setServiceCalibSectionExpanded] = useState(true)
  const [selectedLocalizationMode, setSelectedLocalizationMode] = useState(LOCALIZATION_MODES.UNDEFINED)
  const [selectedLidarMap, setSelectedLidarMap] = useState('')
  const [facilities, setFacilities] = useState<any[]>([])
  const [selectedFacilityId, setSelectedFacilityId] = useState<number | null | undefined>(undefined)
  const [routeName, setRouteName] = useState('')
  const [currentFirmwareVersion, setCurrentFirmwareVersion] = useState<string>('')
  const [latestFirmwareVersion, setLatestFirmwareVersion] = useState<string>('')
  const [currentLocalizationMode, setCurrentLocalizationMode] = useState(LOCALIZATION_MODES.UNDEFINED)
  const lastRobotCurrentMap = useRef<string | undefined>(undefined)
  const lastCurrentLocalizationMode = useRef<LOCALIZATION_MODES | undefined>(undefined)

  const mappingRobotId = robot.robotInfo?.id
  const mappingFacilityId = robot.robotInfo?.facilityId
  useEffect(() => {
    setAllowForceBaseRecord(false)
  }, [mappingRobotId, mappingFacilityId])

  const isOnline = robot.robotInfo?.networkStatus === ROBOT_NETWORK_STATUSES.ONLINE
  const modeServicePoint = activeMode === ROBOT_MODES.MODE_SERVICE_POINT
  const confirmedServiceKey = resolveConfirmedCatalogServiceKey(
    serviceCalib?.catalog ?? null,
    activeMode,
    robot.robotInfo?.activeModeMetadata
  )
  const servicePointUnknown = modeServicePoint && confirmedServiceKey == null
  const forceStopKey = serviceModeActionKey.catalogServiceForceStop()
  const forceStopLoading = useSelector(
    (state: RootState) => state.robot.serviceModeSwitchOptimistic?.actionKey === forceStopKey
  )
  const forceStopTimedOut = useCatalogOperationTimeoutActionKey(robot.robotInfo?.id) === forceStopKey

  useEffect(() => {
    if (!forceStopLoading || !robot.robotInfo?.id) {
      return
    }
    const robotId = robot.robotInfo.id
    const timer = window.setTimeout(() => {
      setCatalogOperationTimeoutNotice(robotId, forceStopKey, activeMode ?? null)
      writeCrossTabCatalogModeBusy(robotId, null)
      dispatch(RobotActions.clearServiceModeSwitchOptimistic())
    }, CATALOG_OPERATION_TIMEOUT_MS)
    return () => window.clearTimeout(timer)
  }, [activeMode, dispatch, forceStopKey, forceStopLoading, robot.robotInfo?.id])

  useEffect(() => {
    if (!forceStopLoading || modeServicePoint) {
      return
    }
    const robotId = robot.robotInfo?.id
    if (robotId) {
      writeCrossTabCatalogModeBusy(robotId, null)
    }
    dispatch(RobotActions.clearServiceModeSwitchOptimistic())
  }, [dispatch, forceStopLoading, modeServicePoint, robot.robotInfo?.id])

  const { isTabActionLoading, isTabActionLoadingOnly } = useServiceTabActionLoading()
  const tab = serviceModeActionKey.tab

  const mappingLoading = isTabActionLoading('mapping')
  const routeRecordingLoading = isTabActionLoading('route-recording')
  const switchLocalizationLoading = isTabActionLoadingOnly('switch-localization')
  const switchLidarMapLoading = isTabActionLoadingOnly('switch-lidar-map')
  const switchFacilityLoading = isTabActionLoadingOnly('switch-facility')
  const firmwareUpdateLoading = isTabActionLoading('firmware-update')
  const rebootLoading = isTabActionLoadingOnly('reboot')

  const modeInProgress =
    activeMode !== ROBOT_MODES.MODE_NOT_SET &&
    activeMode !== ROBOT_MODES.MODE_MANUAL &&
    activeMode !== ROBOT_MODES.MODE_CALIBRATION_SERVICE_POINT &&
    activeMode !== ROBOT_MODES.MODE_RESET_CALIBRATION_SERVICE_POINT &&
    activeMode !== ROBOT_MODES.MODE_SERVICE_POINT
  const modeInMapping = activeMode === ROBOT_MODES.MODE_MAPPING
  const modeInRouteRecording = activeMode === ROBOT_MODES.MODE_ROUTE_RECORDING
  const modeSwitchLocalization =
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_COMBINED_LOCALIZATION ||
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_GNSS_BASED_LOCALIZATION ||
    activeMode === ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION
  const modeUpdatingFirmware = activeMode === ROBOT_MODES.MODE_UPDATING_FIRMWARE
  const deploymentBlockedReason = deploymentTasksBlockedReason({
    activeMode,
    pendingMode: robot.robotInfo?.modeSwitchPending?.mode,
    optimistic: modeSwitchOptimistic,
    catalogOperation,
  })

  // Mapping can confirm after the wait budget; drop the start dialog once MCS shows mapping.
  useEffect(() => {
    if (!confirmStartMappingDialog || !modeInMapping) {
      return
    }
    setMapRecordingError(null)
    setConfirmStartMappingDialog(false)
  }, [confirmStartMappingDialog, modeInMapping, setMapRecordingError])

  // Stop wait may time out before MCS leaves mapping; close once mapping is gone.
  useEffect(() => {
    if (!stopMappingDialog || modeInMapping) {
      return
    }
    setMapRecordingError(null)
    setStopMappingBusy(false)
    setStopMappingDialog(false)
  }, [stopMappingDialog, modeInMapping, setMapRecordingError])

  useEffect(() => {
    if (!stopRouteRecordingDialog || modeInRouteRecording) {
      return
    }
    setRouteRecordingError(null)
    setStopRouteRecordingBusy(false)
    setStopRouteRecordingDialog(false)
  }, [stopRouteRecordingDialog, modeInRouteRecording, setRouteRecordingError])

  const isRobotOnMission = activeMode === ROBOT_MODES.MODE_AUTOMATIC && Boolean(robot.robotInfo?.activeTask)

  const canRebootRobot =
    isOnline &&
    (activeMode === ROBOT_MODES.MODE_NOT_SET ||
      (isRobotOnMission && robot.robotInfo?.activeCommand === ROBOT_COMMANDS.PAUSE))
  const trimmedRouteName = routeName.trim()
  const routeNameIsValid = trimmedRouteName.length > 0 && ROUTE_NAME_ALLOWED_PATTERN.test(trimmedRouteName)

  const getRebootStatusText = () => {
    if (!isOnline) {
      return 'Robot offline'
    }
    if (deploymentBlockedReason) {
      return deploymentBlockedReason
    }
    if (canRebootRobot) return 'Ready to reboot'

    return 'Not available while mission is running'
  }

  const localizationModeOptions: IOption[] = useMemo(
    () =>
      Object.values(LOCALIZATION_MODES)
        .filter(value => typeof value === 'string' && value !== LOCALIZATION_MODES.UNDEFINED)
        .map(mode => ({
          text: mode,
          value: mode,
        })),
    []
  )

  const localizationModeOption = useMemo(() => {
    return (
      localizationModeOptions.find(option => option.value === selectedLocalizationMode) || localizationModeOptions[0]
    )
  }, [localizationModeOptions, selectedLocalizationMode])

  const currentLidarMap = telemetry?.localization_info?.current_lidar_map

  const lidarMapOptions: IOption[] = useMemo(() => {
    return [...(telemetry?.localization_info?.lidar_maps ?? [])].sort().map(map => ({
      text: map,
      value: map,
      isCurrent: map === currentLidarMap,
    }))
  }, [telemetry?.localization_info?.lidar_maps, currentLidarMap])

  const lidarMapOption = lidarMapOptions.find(option => option.value === selectedLidarMap) || {
    text: selectedLidarMap || 'Not set',
    value: selectedLidarMap,
  }

  useEffect(() => {
    MainApi.fetchFacilities().then(res => {
      setFacilities(res.results)
    })
  }, [])

  const currentFacilityIdFromTelemetry =
    telemetry?.facility_id !== undefined && telemetry?.facility_id !== null
      ? Number(telemetry.facility_id)
      : telemetry?.facility_id === null
      ? null
      : undefined
  const currentFacilityId = currentFacilityIdFromTelemetry ?? robot.robotInfo?.facilityId
  const isFacilityMismatch =
    currentFacilityIdFromTelemetry !== undefined &&
    robot.robotInfo?.facilityId !== undefined &&
    currentFacilityIdFromTelemetry !== robot.robotInfo?.facilityId

  const facilityOptions: IOption[] = useMemo(() => {
    const options: IOption[] = [
      {
        text: t('robot.unset_facility'),
        value: null,
        isCurrent: currentFacilityIdFromTelemetry === null,
        isDbCurrent: isFacilityMismatch && robot.robotInfo?.facilityId === null,
      },
    ]
    facilities.forEach(f => {
      options.push({
        text: f.name,
        value: f.id,
        isCurrent: f.id === currentFacilityIdFromTelemetry,
        isDbCurrent: isFacilityMismatch && f.id === robot.robotInfo?.facilityId,
      })
    })
    return options
  }, [facilities, t, currentFacilityIdFromTelemetry, robot.robotInfo?.facilityId])

  const facilityOption = useMemo(() => {
    if (selectedFacilityId !== undefined) {
      return (
        facilityOptions.find(opt => opt.value === selectedFacilityId) || {
          text: String(selectedFacilityId),
          value: selectedFacilityId,
        }
      )
    }
    return (
      facilityOptions.find(opt => opt.value === currentFacilityId) || {
        text:
          facilities.find(f => f.id === currentFacilityId)?.name ||
          (currentFacilityId ? `ID: ${currentFacilityId}` : 'Not set'),
        value: currentFacilityId,
      }
    )
  }, [facilityOptions, selectedFacilityId, currentFacilityId, facilities])

  useEffect(() => {
    if (telemetry?.firmware_info) {
      setCurrentFirmwareVersion(telemetry.firmware_info.current_version)
      setLatestFirmwareVersion(telemetry.firmware_info.latest_version)
    }

    if (telemetry?.localization_info) {
      const currentMode = localiazationModeMapping[telemetry.localization_info.localization_mode] as LOCALIZATION_MODES
      setCurrentLocalizationMode(currentMode)

      if (lastCurrentLocalizationMode.current !== currentMode) {
        if (
          selectedLocalizationMode === LOCALIZATION_MODES.UNDEFINED ||
          selectedLocalizationMode === lastCurrentLocalizationMode.current
        ) {
          setSelectedLocalizationMode(currentMode)
        }
        lastCurrentLocalizationMode.current = currentMode
      }

      const robotMap = telemetry.localization_info.current_lidar_map
      if (lastRobotCurrentMap.current !== robotMap) {
        // If the robot changed its map (or cleared it), sync the UI
        // unless the user has manually picked a different map to switch to
        if (!selectedLidarMap || selectedLidarMap === lastRobotCurrentMap.current) {
          setSelectedLidarMap(robotMap || '')
        }
        lastRobotCurrentMap.current = robotMap
      }
    }
  }, [telemetry, localizationModeOptions, selectedLocalizationMode])

  const onLocalizationModeSelected = (e: IOption) => {
    setSelectedLocalizationMode(e.value as LOCALIZATION_MODES)
  }

  const onLidarMapSelected = (e: IOption) => {
    setSelectedLidarMap(e.value as string)
  }

  const onFacilitySelected = (e: IOption) => {
    setSelectedFacilityId(e.value as number | null)
  }

  const handleModeClick = (mode: boolean, targetMode: ROBOT_MODES, actionId: string) => {
    if (robot.robotInfo?.id !== undefined) {
      if (!mode) {
        store.dispatch(
          robotThunks.setRobotMode(robot.robotInfo.id, targetMode, undefined, {
            actionKey: tab(actionId),
          })
        )
      } else {
        store.dispatch(
          robotThunks.setRobotNotSetMode(robot.robotInfo.id, {
            actionKey: tab(`${actionId}-stop`),
          })
        )
      }
    }
  }

  // Convert yaw from UI format (0 = north, clockwise positive) to radians
  // where 0 = east and counterclockwise is positive (math convention).
  const convertYawToMqttRadians = (uiYaw: number): number => {
    // Convert to user/api angle in degrees first: 0=east, CCW positive
    let yawDegrees = 90 - uiYaw
    while (yawDegrees > 180) yawDegrees -= 360
    while (yawDegrees <= -180) yawDegrees += 360

    return (yawDegrees * Math.PI) / 180
  }

  const buildMappingMetadata = (formData: MappingFormData): RobotModeMetadata | undefined => {
    // Get localization mode from telemetry
    const localizationMode = telemetry?.localization_info?.localization_mode
      ? localiazationModeMapping[telemetry.localization_info.localization_mode]
      : LOCALIZATION_MODES.UNDEFINED
    const isGnssLocalized = localizationMode === LOCALIZATION_MODES.GNSS_BASED_LOCALIZATION

    switch (formData.subMode) {
      case 'update_existing': {
        if (!formData.selectedLocationName) {
          return undefined
        }
        const cleanName = formData.selectedLocationName.replace(/_v\d+$/, '')
        const allMaps = telemetry?.localization_info?.lidar_maps ?? []
        const maxVersion = allMaps.reduce((max, map) => {
          const match = map.match(/^(.+)_v(\d+)$/)
          if (!match || match[1] !== cleanName) return max
          return Math.max(max, parseInt(match[2], 10))
        }, 1)

        return {
          name: `${cleanName}_v${maxVersion + 1}`,
          update_map: true,
          continue_on_the_facility: true,
        }
      }

      case 'new_location':
        if (!formData.locationName?.trim()) {
          return undefined
        }
        return {
          name: formData.locationName.trim(),
          continue_on_the_facility: true,
        }

      case 'new_facility':
      case 'rerecord_base': {
        const facilityName = t('robot.base_map')
        const facilityId = robot.robotInfo?.facilityId

        const baseMetadata: RobotModeMetadata = {
          name: facilityName,
          facility_id: facilityId?.toString() ?? undefined,
          update_map: false,
          continue_on_the_facility: false,
          base_map: true,
        }

        // GNSS mode: no position/orientation required
        if (isGnssLocalized) {
          return baseMetadata
        }

        // Non-GNSS mode: position and orientation are required
        if (!formData.robotPosition || formData.robotYaw === undefined) {
          return undefined
        }

        const [lon, lat] = formData.robotPosition.coordinates

        // Convert UI yaw (0=North, clockwise+) to API yaw (0=East, CCW+) in degrees
        let yawDegrees = 90 - formData.robotYaw
        while (yawDegrees > 180) yawDegrees -= 360
        while (yawDegrees <= -180) yawDegrees += 360

        return {
          ...baseMetadata,
          pose: {
            lon,
            lat,
            yaw: convertYawToMqttRadians(formData.robotYaw),
          },
        }
      }
    }
  }

  const closeMapRecordingDialog = () => {
    mapRecordingWait.abort()
    setConfirmStartMappingDialog(false)
  }

  const onSetMappingModeClicked = async (formData: MappingFormData) => {
    if (robot.robotInfo?.id === undefined) {
      return
    }
    const metadata = buildMappingMetadata(formData)
    if (metadata === undefined) {
      return
    }
    const nonBaseAttempt = formData.subMode === 'new_location' || formData.subMode === 'update_existing'
    store.dispatch(RobotActions.setShowLidarMap(false))
    const outcome = await mapRecordingWait.requestAndWait(
      robot.robotInfo.id,
      ROBOT_MODES.MODE_MAPPING,
      metadata,
      tab('mapping'),
      {
        requestFailed: t('robot.map_recording_request_failed'),
        noConfirmation: t('robot.map_recording_no_confirmation'),
      }
    )
    if (outcome === 'confirmed') {
      setAllowForceBaseRecord(false)
      setConfirmStartMappingDialog(false)
      return
    }
    const stayedIdle = store.getState().robot.robotInfo?.activeMode === ROBOT_MODES.MODE_NOT_SET
    if (nonBaseAttempt && outcome === 'timeout' && stayedIdle) {
      setAllowForceBaseRecord(true)
    }
  }

  const closeStopMappingDialog = () => {
    mapRecordingWait.abort()
    setStopMappingBusy(false)
    setStopMappingDialog(false)
    setMapRecordingError(null)
  }

  const runStopMappingWait = async () => {
    if (robot.robotInfo?.id === undefined) {
      return
    }
    setStopMappingBusy(true)
    const outcome = await mapRecordingWait.requestStopAndWait(
      robot.robotInfo.id,
      tab('mapping-stop'),
      {
        requestFailed: t('robot.map_recording_stop_request_failed'),
        noConfirmation: t('robot.map_recording_stop_no_confirmation'),
      },
      { longerWait: true }
    )
    setStopMappingBusy(false)
    if (outcome === 'confirmed' || outcome === 'aborted') {
      setStopMappingDialog(false)
    }
  }

  const onStopMappingModeClicked = () => {
    setMapRecordingError(null)
    setStopMappingDialog(true)
    void runStopMappingWait()
  }

  const closeRouteRecordingDialog = () => {
    routeRecordingWait.abort()
    setConfirmStartRoutingDialog(false)
    setRouteName('')
  }

  const closeStopRouteRecordingDialog = () => {
    routeRecordingWait.abort()
    setStopRouteRecordingBusy(false)
    setStopRouteRecordingDialog(false)
    setRouteRecordingError(null)
  }

  const runStopRouteRecordingWait = async () => {
    if (robot.robotInfo?.id === undefined) {
      return
    }
    setStopRouteRecordingBusy(true)
    const outcome = await routeRecordingWait.requestStopAndWait(
      robot.robotInfo.id,
      tab('route-recording-stop'),
      {
        requestFailed: t('robot.route_recording_stop_request_failed'),
        noConfirmation: t('robot.route_recording_stop_no_confirmation'),
      },
      { longerWait: true }
    )
    setStopRouteRecordingBusy(false)
    if (outcome === 'confirmed' || outcome === 'aborted') {
      setStopRouteRecordingDialog(false)
    }
  }

  const onStopRouteRecordingModeClicked = () => {
    setRouteRecordingError(null)
    setStopRouteRecordingDialog(true)
    void runStopRouteRecordingWait()
  }

  const onSetRouteRecordingModeClicked = async () => {
    if (robot.robotInfo?.id === undefined) {
      return
    }
    const outcome = await routeRecordingWait.requestAndWait(
      robot.robotInfo.id,
      ROBOT_MODES.MODE_ROUTE_RECORDING,
      { name: trimmedRouteName },
      tab('route-recording'),
      {
        requestFailed: t('robot.route_recording_request_failed'),
        noConfirmation: t('robot.route_recording_no_confirmation'),
      }
    )
    if (outcome === 'confirmed') {
      setConfirmStartRoutingDialog(false)
      setRouteName('')
    }
  }

  const onSwitchLocalizationClicked = () => {
    handleModeClick(
      modeSwitchLocalization,
      localiazationModeToRobotModesMapping[selectedLocalizationMode],
      'switch-localization'
    )
    setConfirmSwitchLocalizationDialog(false)
  }

  const onUpdateFirmwareClicked = () => {
    handleModeClick(modeUpdatingFirmware, ROBOT_MODES.MODE_UPDATING_FIRMWARE, 'firmware-update')
    setConfirmUpdatingFirmwareDialog(false)
  }

  const onSwitchLidarMapClicked = async () => {
    if (robot.robotInfo?.id !== undefined && selectedLidarMap) {
      try {
        store.dispatch(RobotActions.setShowLidarMap(false))
        await store.dispatch(
          robotThunks.setRobotMode(
            robot.robotInfo.id,
            ROBOT_MODES.MODE_SWITCHING_TO_LIDAR_LOCALIZATION,
            {
              name: selectedLidarMap,
            },
            {
              actionKey: tab('switch-lidar-map'),
            }
          )
        )
      } catch (error) {
        // Error handled upstream
      }
    }
    setConfirmSwitchLidarMapDialog(false)
  }

  const onSwitchFacilityClicked = async () => {
    if (robot.robotInfo?.id !== undefined) {
      const targetFacilityId = selectedFacilityId === undefined ? currentFacilityId : selectedFacilityId
      try {
        await RobotApi.patchRobot(
          robot.robotInfo.id,
          robot.robotInfo.name,
          robot.robotInfo.description,
          robot.robotInfo.sim_card_number,
          robot.robotInfo.robot_type,
          robot.robotInfo.robot_color,
          robot.robotInfo.vendor,
          targetFacilityId
        )
        if (targetFacilityId !== undefined) {
          await store.dispatch(
            robotThunks.setRobotMode(
              robot.robotInfo.id,
              ROBOT_MODES.MODE_SWITCHING_FACILITY,
              {
                facility_id: String(targetFacilityId),
              },
              {
                actionKey: tab('switch-facility'),
              }
            )
          )
        }
        store.dispatch(robotThunks.fetchRobotSilent(robot.robotInfo.id))
        setSelectedFacilityId(undefined)
      } catch (error) {
        // Error handled upstream
      }
    }
    setConfirmSwitchFacilityDialog(false)
  }

  const onRebootRobotClicked = () => {
    if (robot.robotInfo?.id !== undefined) {
      store.dispatch(
        robotThunks.setRobotMode(robot.robotInfo.id, ROBOT_MODES.MODE_REBOOT_ROBOT, undefined, {
          actionKey: tab('reboot'),
        })
      )
    }
    setConfirmRebootDialog(false)
  }

  return (
    <div className={style.root}>
      <ServiceReportRow
        serialNumber={robot.robotInfo?.serialNumber}
        robotType={robot.robotInfo?.robot_type}
        robotTypeDisplay={robot.robotInfo?.robot_type_display}
        vendor={robot.robotInfo?.vendor}
      />
      <div className={calibStyle.stationGroup}>
        <div className={calibStyle.groupToggler}>
          <button
            type="button"
            className={calibStyle.groupTogglerMain}
            onClick={() => setServiceCalibSectionExpanded(expanded => !expanded)}
            aria-expanded={serviceCalibSectionExpanded}
          >
            <div className={calibStyle.groupTogglerIcon}>
              <ArrowSvg
                className={clsx(calibStyle.groupArrow, serviceCalibSectionExpanded && calibStyle.groupArrowExpanded)}
              />
            </div>
            <span className={calibStyle.stationGroupName}>Service points</span>
          </button>
          <div className={calibStyle.catalogMetaCell}>
            {servicePointUnknown && <span className={calibStyle.unknownServicePointHint}>Service point unknown</span>}
          </div>
          <div className={calibStyle.calibActions}>
            {servicePointUnknown && robot.robotInfo?.id != null && (
              <Button.Outlined
                className={calibStyle.forceStopButton}
                loading={forceStopLoading}
                disabled={forceStopLoading || !isOnline}
                title={
                  forceStopTimedOut ? 'No confirmation' : isOnline ? 'Stop the service action' : 'Robot is offline'
                }
                onClick={() => {
                  store.dispatch(robotThunks.setRobotNotSetMode(robot.robotInfo!.id, { actionKey: forceStopKey }))
                }}
              >
                Force stop
              </Button.Outlined>
            )}
          </div>
        </div>
        {serviceCalibSectionExpanded &&
          (robot.robotInfo?.facilityId && serviceCalib ? (
            <ServiceCalibCatalog {...serviceCalib} isOnline={isOnline} />
          ) : robot.robotInfo?.facilityId ? (
            <div className={style.catalogEmptyHint}>Loading service calibration catalog…</div>
          ) : (
            <div className={style.catalogEmptyHint}>
              Attach the robot to a facility to calibrate station lots and maintenance points.
            </div>
          ))}
      </div>

      <div className={style.tableHeaderRow}>
        <div className={style.centered}>Deployment Tasks</div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <MappingSvg className={style.rowTaskIcon} />
        </div>
        <div
          className={clsx(style.chip, style.centered, style.locked)}
          title={
            deploymentBlockedReason ??
            (isFacilityMismatch && !modeInMapping
              ? 'Facility is not matched'
              : !robot.robotInfo?.facilityId
              ? 'The robot must be attached to a facility to start deployment'
              : undefined)
          }
        >
          <Button.Standart
            className={style.button}
            loading={mappingLoading}
            disabled={
              Boolean(deploymentBlockedReason) ||
              (isFacilityMismatch && !modeInMapping) ||
              (modeInProgress && !modeInMapping) ||
              (!modeInMapping && !isOnline) ||
              (!modeInMapping && !robot.robotInfo?.facilityId)
            }
            onClick={() => {
              if (modeInMapping) {
                onStopMappingModeClicked()
                return
              }
              mapRecordingWait.begin()
              setConfirmStartMappingDialog(true)
            }}
          >
            {!modeInMapping ? 'Start Map Recording' : 'Stop Map Recording'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>
          {modeInMapping ? 'Mapping in progress...' : ''}
        </div>
      </div>

      <div className={style.tableRow}>
        <div className={style.centered}>
          <RoutingSvg className={style.rowTaskIcon} />
        </div>
        <div
          className={clsx(style.chip, style.centered, style.locked)}
          title={
            deploymentBlockedReason ??
            (isFacilityMismatch && !modeInRouteRecording ? 'Facility is not matched' : undefined)
          }
        >
          <Button.Standart
            className={style.button}
            loading={routeRecordingLoading}
            disabled={
              Boolean(deploymentBlockedReason) ||
              (isFacilityMismatch && !modeInRouteRecording) ||
              (modeInProgress && !modeInRouteRecording) ||
              !isOnline
            }
            onClick={() =>
              modeInRouteRecording
                ? onStopRouteRecordingModeClicked()
                : (() => {
                    routeRecordingWait.begin()
                    setConfirmStartRoutingDialog(true)
                  })()
            }
          >
            {!modeInRouteRecording ? 'Start Route Recording' : 'Stop Route Recording'}
          </Button.Standart>
        </div>
        <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>
          {modeInRouteRecording ? 'Route recording in progress...' : ''}
        </div>
      </div>

      <>
        <div className={style.tableRow}>
          <div className={style.centered}>
            <LocalizationSvg className={style.rowTaskIcon} />
          </div>
          <div
            className={clsx(style.chip, style.centered, style.locked)}
            title={deploymentBlockedReason ?? (isFacilityMismatch ? 'Facility is not matched' : undefined)}
          >
            <Button.Standart
              className={style.button}
              loading={switchLocalizationLoading}
              disabled={
                Boolean(deploymentBlockedReason) ||
                modeInProgress ||
                modeSwitchLocalization ||
                selectedLocalizationMode === LOCALIZATION_MODES.UNDEFINED ||
                currentLocalizationMode === selectedLocalizationMode ||
                selectedLocalizationMode === LOCALIZATION_MODES.COMBINED_LOCALIZATION ||
                !isOnline
              }
              onClick={() => setConfirmSwitchLocalizationDialog(true)}
            >
              {selectedLocalizationMode === LOCALIZATION_MODES.COMBINED_LOCALIZATION
                ? 'Coming soon'
                : !modeSwitchLocalization
                ? 'Switch Localization Mode'
                : 'Switching Localization in Progress'}
            </Button.Standart>
          </div>
          <div className={clsx(style.chip, style.centered, style.locked)} title={deploymentBlockedReason ?? undefined}>
            <Dropdown
              className={style.textfield}
              disabled={!isOnline || Boolean(deploymentBlockedReason)}
              currentOption={localizationModeOption}
              options={localizationModeOptions}
              onClickOption={onLocalizationModeSelected}
              minWidth="max-content"
            />
          </div>
        </div>

        <div className={style.tableRow}>
          <div className={style.centered}>
            <MapUpdateSvg className={style.rowTaskIcon} />
          </div>
          <div
            className={clsx(style.chip, style.centered, style.locked)}
            title={deploymentBlockedReason ?? (isFacilityMismatch ? 'Facility is not matched' : undefined)}
          >
            <Button.Standart
              className={style.button}
              loading={switchLidarMapLoading}
              disabled={
                Boolean(deploymentBlockedReason) ||
                isFacilityMismatch ||
                !isOnline ||
                modeInProgress ||
                !selectedLidarMap ||
                selectedLidarMap === telemetry?.localization_info?.current_lidar_map ||
                currentLocalizationMode !== LOCALIZATION_MODES.LIDAR_LOCALIZATION
              }
              onClick={() => setConfirmSwitchLidarMapDialog(true)}
            >
              {t('robot.button_switch_map')}
            </Button.Standart>
          </div>
          <div className={clsx(style.chip, style.centered, style.locked)} title={deploymentBlockedReason ?? undefined}>
            <Dropdown
              className={style.textfield}
              disabled={!isOnline || Boolean(deploymentBlockedReason)}
              currentOption={lidarMapOption}
              options={lidarMapOptions}
              onClickOption={onLidarMapSelected}
              minWidth="max-content"
            />
          </div>
        </div>
        <div className={style.tableRow}>
          <div className={style.centered}>
            <FacilitySvg className={style.rowTaskIcon} />
          </div>
          <div className={clsx(style.chip, style.centered, style.locked)} title={deploymentBlockedReason ?? undefined}>
            <Button.Standart
              className={style.button}
              loading={switchFacilityLoading}
              disabled={
                Boolean(deploymentBlockedReason) ||
                !isOnline ||
                modeInProgress ||
                selectedFacilityId === undefined ||
                (selectedFacilityId === robot.robotInfo?.facilityId &&
                  selectedFacilityId === currentFacilityIdFromTelemetry)
              }
              onClick={() => setConfirmSwitchFacilityDialog(true)}
            >
              {t('robot.button_switch_facility')}
            </Button.Standart>
          </div>
          <div className={clsx(style.chip, style.centered, style.locked)} title={deploymentBlockedReason ?? undefined}>
            <Dropdown
              className={style.textfield}
              disabled={!isOnline || Boolean(deploymentBlockedReason)}
              currentOption={facilityOption}
              options={facilityOptions}
              onClickOption={onFacilitySelected}
              minWidth="max-content"
            />
          </div>
        </div>
        {telemetry?.ntrip != null && (
          <div className={style.tableRow}>
            <div className={style.centered}>
              <SatelliteSvg className={style.rowTaskIcon} />
            </div>
            <div
              className={clsx(style.chip, style.centered, style.locked)}
              title={deploymentBlockedReason ?? undefined}
            >
              <Button.Standart
                className={style.button}
                disabled={Boolean(deploymentBlockedReason) || !isOnline || modeInProgress}
                onClick={() => setNtripDialogOpen(true)}
              >
                {t('robot.ntrip_button')}
              </Button.Standart>
            </div>
            <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>
              <span>
                {telemetry.ntrip.status != null &&
                telemetry.ntrip.status !== '' &&
                !isNtripConnected(telemetry.ntrip.status) ? (
                  <>
                    {telemetry.ntrip.status}
                    {telemetry.ntrip.message ? (
                      <>
                        <br />
                        {telemetry.ntrip.message}
                      </>
                    ) : null}
                  </>
                ) : (
                  <>
                    {telemetry.ntrip.caster || '—'}
                    <br />
                    {telemetry.ntrip.mountpoint || 'AUTO'}
                  </>
                )}
              </span>
            </div>
          </div>
        )}
        <div className={style.tableHeaderRow}>
          <div className={style.centered}>Service Tasks</div>
        </div>
        <div className={style.tableRow}>
          <div className={style.centered}>
            <FirmwareUpdateSvg className={style.rowTaskIcon} />
          </div>
          <div className={clsx(style.chip, style.centered, style.locked)} title={deploymentBlockedReason ?? undefined}>
            <Button.Standart
              className={style.button}
              loading={firmwareUpdateLoading}
              disabled={
                Boolean(deploymentBlockedReason) ||
                modeInProgress ||
                modeUpdatingFirmware ||
                currentFirmwareVersion === latestFirmwareVersion ||
                !isOnline
              }
              onClick={() => setConfirmUpdatingFirmwareDialog(true)}
            >
              {!modeUpdatingFirmware ? 'Firmware Update' : 'Firmware Update is in Progress'}
            </Button.Standart>
          </div>
          <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>
            {currentFirmwareVersion !== latestFirmwareVersion ? (
              <>
                Current: {currentFirmwareVersion} <br /> Available: {latestFirmwareVersion}
              </>
            ) : (
              `Current: ${currentFirmwareVersion}`
            )}
          </div>
        </div>

        {canSeeRebootButton && (
          <div className={style.tableRow}>
            <div className={style.centered}>
              <UpdateSvg className={style.rowTaskIcon} />
            </div>
            <div
              className={clsx(style.chip, style.centered, style.locked)}
              title={deploymentBlockedReason ?? undefined}
            >
              <Button.Standart
                className={style.button}
                loading={rebootLoading}
                disabled={
                  Boolean(deploymentBlockedReason) ||
                  rebootLoading ||
                  !canRebootRobot ||
                  !isOnline ||
                  modeUpdatingFirmware
                }
                onClick={() => setConfirmRebootDialog(true)}
              >
                Reboot Robot
              </Button.Standart>
            </div>
            <div className={clsx(style.chipSmallFont, style.centered, style.locked)}>{getRebootStatusText()}</div>
          </div>
        )}
      </>

      <MapRecordingModal
        isOpen={confirmStartMappingDialog}
        onClose={closeMapRecordingDialog}
        onConfirm={onSetMappingModeClicked}
        robotInfo={robot.robotInfo}
        confirmError={mapRecordingWait.error}
        allowForceBaseRecord={allowForceBaseRecord}
      />

      <ConfirmDialog
        title={t('robot.map_recording_stopping_title')}
        isOpen={stopMappingDialog}
        onClose={closeStopMappingDialog}
        onConfirmClick={runStopMappingWait}
        confirmText={mapRecordingWait.error ? t('common.retry') : t('common.waiting')}
        confirmButtonDisabled={stopMappingBusy && !mapRecordingWait.error}
        busy={stopMappingBusy}
        error={mapRecordingWait.error}
      >
        <div className={style.confirmField}>{t('robot.map_recording_stopping_body')}</div>
      </ConfirmDialog>

      <ConfirmDialog
        title={'Start route recording?'}
        isOpen={confirmStartRoutingDialog}
        onClose={closeRouteRecordingDialog}
        onConfirmClick={onSetRouteRecordingModeClicked}
        confirmText={'Confirm'}
        confirmButtonDisabled={!routeNameIsValid}
        danger={false}
        error={routeRecordingWait.error}
      >
        <div className={style.confirmField}>
          <label className={style.confirmLabel} htmlFor="route-recording-name">
            Route name
          </label>
          <input
            id="route-recording-name"
            className={clsx(style.confirmInput, routeName && !routeNameIsValid && style.confirmInputError)}
            value={routeName}
            autoFocus
            placeholder="Enter route name"
            onChange={event => setRouteName(event.target.value)}
          />
          {routeName && !routeNameIsValid && (
            <div className={style.confirmError}>Allowed: A-Z, 0-9, dash, underscore, parentheses, slash, space.</div>
          )}
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        title={t('robot.route_recording_stopping_title')}
        isOpen={stopRouteRecordingDialog}
        onClose={closeStopRouteRecordingDialog}
        onConfirmClick={runStopRouteRecordingWait}
        confirmText={routeRecordingWait.error ? t('common.retry') : t('common.waiting')}
        confirmButtonDisabled={stopRouteRecordingBusy && !routeRecordingWait.error}
        busy={stopRouteRecordingBusy}
        error={routeRecordingWait.error}
      >
        <div className={style.confirmField}>{t('robot.route_recording_stopping_body')}</div>
      </ConfirmDialog>

      <ConfirmDialog
        title={'Switching the localization mode'}
        isOpen={confirmSwitchLocalizationDialog}
        onClose={() => setConfirmSwitchLocalizationDialog(false)}
        onConfirmClick={() => onSwitchLocalizationClicked()}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Firmware update'}
        isOpen={confirmUpdatingFirmwareDialog}
        onClose={() => setConfirmUpdatingFirmwareDialog(false)}
        onConfirmClick={onUpdateFirmwareClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={'Reboot the robot?'}
        isOpen={confirmRebootDialog}
        onClose={() => setConfirmRebootDialog(false)}
        onConfirmClick={onRebootRobotClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <ConfirmDialog
        title={t('robot.confirm_switch_lidar_map_title')}
        isOpen={confirmSwitchLidarMapDialog}
        onClose={() => setConfirmSwitchLidarMapDialog(false)}
        onConfirmClick={onSwitchLidarMapClicked}
        confirmText={'Confirm'}
        danger={true}
      />

      <NtripSettingsDialog
        isOpen={ntripDialogOpen}
        robotId={robot.robotInfo?.id}
        onClose={() => setNtripDialogOpen(false)}
      />

      <ConfirmDialog
        title={t('robot.confirm_switch_facility_title')}
        isOpen={confirmSwitchFacilityDialog}
        onClose={() => setConfirmSwitchFacilityDialog(false)}
        onConfirmClick={onSwitchFacilityClicked}
        confirmText={'Confirm'}
        danger={true}
      >
        <div style={{ padding: '10px 0', fontSize: '14px', color: 'var(--text-main)' }}>
          {selectedFacilityId === null
            ? t('robot.confirm_switch_facility_body_unset')
            : t('robot.confirm_switch_facility_body', {
                current: facilities.find(f => f.id === currentFacilityId)?.name || 'Not set',
                target: facilityOption.text,
              })}
        </div>
      </ConfirmDialog>
    </div>
  )
}

export default Service
